// Everything sits in the texture container's own space, so its flips and scale apply.
import * as PIXI from 'pixi.js';
import { game } from '../../Game';
import { Settings } from '../../Settings';
import { boneAxis, clipHalf, frameRect } from './goreGeometry';
import { artOf, css, destroyMesh, extrasOf, makeCanvas, meshOf, paint, shapeBounds, tracePath } from './goreArt';
import { PALETTE } from './goreShapes';
import { slicePiece } from './goreSlice';

const owned = [];

const entryOf = container => {
	let entry = owned.find(e => e.container === container);
	if (!entry) owned.push(entry = {container, layers: [], kept: []});
	return entry;
};

const releaseStale = () => {
	for (let i = owned.length - 1; i >= 0; i--) {
		if (!owned[i].container.destroyed) continue;
		[...owned[i].layers, ...owned[i].kept].forEach(texture => texture.destroy(true));
		owned.splice(i, 1);
	}
};

const releaseLayers = container => {
	const entry = owned.find(e => e.container === container);
	if (!entry) return;
	entry.layers.forEach(texture => texture.destroy(true));
	entry.layers.length = 0;
};

// Mirrors updateBodyPosition, so it holds for every pose of the body.
export const artMapping = skin => {
	const data = skin.data;
	let offsetAngle = data.texturePositionOffsetAngle;
	if (data.mirrored) offsetAngle = Math.atan2(Math.sin(offsetAngle), -Math.cos(offsetAngle));
	const ox = data.texturePositionOffsetLength * Math.cos(offsetAngle);
	const oy = -data.texturePositionOffsetLength * Math.sin(offsetAngle);
	const c = Math.cos(data.textureAngleOffset);
	const s = Math.sin(data.textureAngleOffset);
	const {x: sx, y: sy} = skin.scale;
	const {x: px, y: py} = skin.pivot;
	const ptm = Settings.PTM;
	return {
		point: (x, y) => {
			const qx = x * ptm - ox;
			const qy = y * ptm - oy;
			return {x: px + (c * qx - s * qy) / sx, y: py + (s * qx + c * qy) / sy};
		},
		vector: (x, y) => ({x: (c * x - s * y) / sx, y: (s * x + c * y) / sy}),
	};
};

const stateOf = skin => skin.goreCut || {
	cell: {poly: frameRect(Math.round(skin.originalSprite.texture.orig.width), Math.round(skin.originalSprite.texture.orig.height)), sides: {}},
	lines: [],
	skinTexture: skin.originalSprite.texture,
	skinTint: skin.originalSprite.tint,
	fleshTexture: skin.myFlesh?.texture,
	fleshTint: skin.myFlesh?.tint ?? 0xffffff,
	extras: extrasOf(skin).map(child => ({texture: child.texture, x: child.x - child.anchor.x * child.texture.orig.width, y: child.y - child.anchor.y * child.texture.orig.height, tint: child.tint})),
};

export const cutArt = (skin, limb) => {
	if (!skin || skin.destroyed || !skin.originalSprite || !skin.data?.textureName || !game.app?.renderer) return null;
	const art = artOf(PIXI.Texture.from(skin.data.textureName));
	if (art && limb && art.bone === undefined) art.bone = boneAxis(art.rgba, art.w, art.h);
	return art;
};

const layer = (container, box, draw) => {
	const x = Math.floor(box.minX);
	const y = Math.floor(box.minY);
	const w = Math.max(1, Math.ceil(box.maxX) - x);
	const h = Math.max(1, Math.ceil(box.maxY) - y);
	const canvas = makeCanvas(w, h);
	const ctx = canvas.getContext('2d');
	ctx.translate(-x, -y);
	ctx.lineCap = ctx.lineJoin = 'round';
	draw(ctx);
	const texture = PIXI.Texture.from(canvas);
	entryOf(container).layers.push(texture);
	const sprite = new PIXI.Sprite(texture);
	sprite.position.set(x, y);
	return sprite;
};

const keepToArt = (ctx, art) => {
	ctx.globalCompositeOperation = 'destination-in';
	ctx.drawImage(art.canvas, 0, 0);
	ctx.globalCompositeOperation = 'source-over';
};

const dress = (container, piece, state, art) => {
	for (const child of container.goreDisplay || []) {
		container.removeChild(child);
		if (child instanceof PIXI.Mesh) destroyMesh(child);
		else child.destroy();
	}
	releaseLayers(container);
	const outerBox = shapeBounds(piece.outer.map(v => ({x: v.x, y: v.y, r: 0.5})));
	const under = layer(container, shapeBounds(piece.under, {...outerBox}), ctx => {
		ctx.save();
		ctx.beginPath();
		tracePath(ctx, piece.outer);
		ctx.clip();
		ctx.beginPath();
		tracePath(ctx, piece.outer);
		if (piece.inner) tracePath(ctx, piece.inner);
		ctx.fillStyle = css(PALETTE.meat);
		ctx.fill('evenodd');
		paint(ctx, piece.under);
		ctx.restore();
		keepToArt(ctx, art);
	});
	const over = layer(container, shapeBounds(piece.overlay, shapeBounds(piece.over, {...outerBox})), ctx => {
		ctx.save();
		ctx.beginPath();
		tracePath(ctx, piece.outer);
		ctx.clip();
		paint(ctx, piece.over);
		ctx.restore();
		keepToArt(ctx, art);
		paint(ctx, piece.overlay);
	});
	const flesh = state.fleshTexture && meshOf(piece.outer, state.fleshTexture, state.fleshTint);
	const skin = meshOf(piece.inner, state.skinTexture, state.skinTint);
	const extras = state.extras.map(extra => meshOf(piece.inner, extra.texture, extra.tint, extra.x, extra.y));
	const display = [flesh, under, skin, ...extras, over].filter(Boolean);
	container.addChild(...display);
	container.goreDisplay = display;
	container.originalSprite = skin || container.originalSprite;
	container.myFlesh = flesh || container.myFlesh;
};

// Both halves share one decal texture: without its own copy, wounds stamped
// near the cut on one half would show on the other.
const snapshotSkin = (state, art) => {
	const target = PIXI.RenderTexture.create({width: art.w, height: art.h});
	const scene = new PIXI.Container();
	scene.addChild(new PIXI.Sprite(state.skinTexture));
	for (const extra of state.extras) {
		const sprite = new PIXI.Sprite(extra.texture);
		sprite.position.set(extra.x, extra.y);
		sprite.tint = extra.tint;
		scene.addChild(sprite);
	}
	game.app.renderer.render({container: scene, target, clear: true});
	scene.destroy({children: true, texture: false, textureSource: false});
	return target;
};

// `line` and `keptPoint` are in the body's local space.
export const dressCut = ({skin, farSkin, line, keptPoint, up, inside, organs, art, blood}) => {
	releaseStale();
	const state = stateOf(skin);
	const map = artMapping(skin);
	const p = map.point(line.px, line.py);
	const d = map.vector(line.dx, line.dy);
	const length = Math.hypot(d.x, d.y) || 1;
	const artLine = {id: state.lines.length, px: p.x, py: p.y, nx: -d.y / length, ny: d.x / length};
	const kept = map.point(keptPoint.x, keptPoint.y);
	const keptSide = (kept.x - artLine.px) * artLine.nx + (kept.y - artLine.py) * artLine.ny > 0 ? -1 : 1;
	const keptPoly = clipHalf(state.cell.poly, artLine, keptSide);
	const farPoly = clipHalf(state.cell.poly, artLine, -keptSide);
	if (!keptPoly || !farPoly) return false;
	const lines = [...state.lines, artLine];
	const upArt = map.vector(up.x, up.y);
	const upLength = Math.hypot(upArt.x, upArt.y) || 1;
	const options = {inside, organs, bone: art.bone || null, up: {x: upArt.x / upLength, y: upArt.y / upLength}, blood};
	const keptState = {...state, cell: {poly: keptPoly, sides: {...state.cell.sides, [artLine.id]: keptSide}}, lines};
	const farState = {...state, cell: {poly: farPoly, sides: {...state.cell.sides, [artLine.id]: -keptSide}}, lines, extras: []};
	farState.skinTexture = snapshotSkin(state, art);
	farState.skinTint = 0xffffff;
	entryOf(farSkin).kept.push(farState.skinTexture);
	const keptPiece = slicePiece(art, keptState.cell, lines, {...options, seed: Math.floor(Math.random() * 0x7fffffff)});
	const farPiece = slicePiece(art, farState.cell, lines, {...options, seed: Math.floor(Math.random() * 0x7fffffff)});
	if (!skin.goreCut) {
		for (const child of [skin.originalSprite, skin.myFlesh, ...extrasOf(skin)]) {
			if (!child || child.destroyed) continue;
			skin.removeChild(child);
			child.destroy();
		}
	}
	dress(skin, keptPiece, keptState, art);
	dress(farSkin, farPiece, farState, art);
	skin.goreCut = keptState;
	farSkin.goreCut = farState;
	const textures = game.editor.textures;
	const front = farPiece.capped && !keptPiece.capped ? farSkin : skin;
	const back = front === skin ? farSkin : skin;
	if (back.parent === textures && front.parent === textures && textures.getChildIndex(front) < textures.getChildIndex(back)) {
		textures.setChildIndex(front, textures.getChildIndex(back));
	}
	return true;
};
